<template>
  <div v-if="isActive" class="tutorial-overlay">
    <!-- Overlay de fondo -->
    <div class="tutorial-backdrop" @click="closeTutorial"></div>

    <!-- Contenedor principal del tutorial -->
    <div
      class="tutorial-container"
      :class="{
        'tutorial-active': isActive,
        'tutorial-minimized': shouldShowMinimized,
      }"
      :data-tutorial-type="tutorialType"
    >
      <!-- Header del tutorial -->
      <div class="tutorial-header">
        <div class="tutorial-progress">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: progressPercentage + '%' }"
            ></div>
          </div>
          <span class="progress-text"
            >{{ currentStep + 1 }} {{ $t("tutorial.progress") }}
            {{ tutorialSteps.length }}</span
          >
        </div>

        <div class="tutorial-controls">
          <button
            class="btn btn-sm btn-primary me-2"
            @click="previousStep"
            :disabled="currentStep === 0"
          >
            <i class="fas fa-chevron-left"></i>
            {{ $t("tutorial.navigation.previous") }}
          </button>

          <button
            v-if="currentStep < tutorialSteps.length - 1"
            class="btn btn-sm btn-primary me-2"
            @click="nextStep"
          >
            {{ $t("tutorial.navigation.next") }}
            <i class="fas fa-chevron-right"></i>
          </button>

          <button
            v-else
            class="btn btn-sm btn-success me-2"
            @click="completeTutorial"
          >
            <i class="fas fa-check"></i>
            {{ $t("tutorial.navigation.complete") }}
          </button>

          <button class="btn btn-sm btn-outline-danger" @click="closeTutorial">
            <i class="fas fa-times"></i> {{ $t("tutorial.navigation.exit") }}
          </button>
        </div>
      </div>

      <!-- Contenido del paso actual -->
      <div class="tutorial-content">
        <div class="step-content">
          <h4 class="step-title">
            <i :class="currentStepData.icon" class="me-2"></i>
            {{ getStepTitle() }}
          </h4>

          <div class="step-description">
            {{ getStepDescription() }}
          </div>

          <div v-if="hasStepDetails()" class="step-details">
            <div
              v-for="(detail, index) in getStepDetails()"
              :key="index"
              class="detail-item"
            >
              <i class="fas fa-check-circle text-success me-2"></i>
              {{ detail }}
            </div>
          </div>

          <div v-if="hasStepTip()" class="step-tip">
            <div class="tip-header">
              <i class="fas fa-lightbulb text-warning me-2"></i>
              <strong>{{ $t("tutorial.tipLabel") }}</strong>
            </div>
            <div class="tip-content">{{ getStepTip() }}</div>
          </div>
        </div>
      </div>

      <!-- Indicadores de navegación -->
      <div class="tutorial-navigation">
        <div class="step-indicators">
          <div
            v-for="(step, index) in tutorialSteps"
            :key="index"
            class="step-indicator"
            :class="{
              active: index === currentStep,
              completed: index < currentStep,
            }"
            @click="goToStep(index)"
          >
            <i v-if="index < currentStep" class="fas fa-check"></i>
            <span v-else>{{ index + 1 }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Highlight del elemento actual -->
    <div
      v-if="currentStepData.element && currentStepData.element !== 'none'"
      class="tutorial-highlight"
      :style="getHighlightPosition()"
    ></div>
  </div>
</template>

<script>
export default {
  name: "TutorialOverlay",

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    modelName: {
      type: String,
      default: "",
    },
    tutorialType: {
      type: String,
      default: "listings", // Solo 'listings' ahora
      validator: (value) => ["listings"].includes(value),
    },
    customSteps: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      currentStep: 0,
      isMinimized: false,
      listingsSteps: [
        {
          key: "welcome",
          icon: "fas fa-graduation-cap",
          element: "none",
          showMinimized: false, // No minimizar para introducción
        },
        {
          key: "search",
          icon: "fas fa-search",
          element: ".filters-container .input-group",
          showMinimized: true, // Minimizar para explicar elemento específico
        },
        {
          key: "columnPreferences",
          icon: "fas fa-columns",
          element: 'button[title*="column"], button:has(i.fas.fa-columns)',
          showMinimized: true, // Minimizar para explicar elemento específico
        },
        {
          key: "filterPreferences",
          icon: "fas fa-filter",
          element: 'button:has(i.fas.fa-filter), button[title*="filter"]',
          showMinimized: true, // Minimizar para explicar elemento específico
        },
        {
          key: "filterUsage",
          icon: "fas fa-toggle-on",
          element: ".filters-container .filter-button",
          showMinimized: true, // Minimizar para explicar elemento específico
        },
        {
          key: "columnSorting",
          icon: "fas fa-sort",
          element: ".sortable-header",
          showMinimized: true, // Minimizar para explicar elemento específico
        },
        {
          key: "individualColumns",
          icon: "fas fa-cog",
          element: ".hide-column-btn",
          showMinimized: true, // Minimizar para explicar elemento específico
        },

        {
          key: "preferencesSystem",
          icon: "fas fa-save",
          element: "none",
          showMinimized: false, // No minimizar para explicación general
        },
        {
          key: "reportViews",
          icon: "fas fa-file-alt",
          element: 'button[title*="report"]',
          showMinimized: true, // Minimizar para explicar elemento específico
        },
        {
          key: "bulkActions",
          icon: "fas fa-tasks",
          element: ".btn-warning",
          showMinimized: true, // Minimizar para explicar elemento específico
        },
        {
          key: "completion",
          icon: "fas fa-trophy",
          element: "none",
          showMinimized: false, // No minimizar para finalización
        },
      ],
      calendarSteps: [
        {
          key: "calendarWelcome",
          icon: "fas fa-calendar-alt",
          element: "none",
          showMinimized: false,
        },
        {
          key: "calendarNavigation",
          icon: "fas fa-chevron-left",
          element: "button.btn-primary",
          showMinimized: true,
        },
        {
          key: "calendarDays",
          icon: "fas fa-calendar-day",
          element: ".calendar-day",
          showMinimized: true,
        },
        {
          key: "eventDisplay",
          icon: "fas fa-calendar-check",
          element: ".event-content",
          showMinimized: true,
        },
        {
          key: "shippingColors",
          icon: "fas fa-palette",
          element:
            ".status-grey, .status-yellow, .status-orange, .status-blue, .status-red, .status-green",
          showMinimized: true,
        },
        {
          key: "colorMeaning",
          icon: "fas fa-info-circle",
          element: "none",
          showMinimized: false,
        },
        {
          key: "dayInteraction",
          icon: "fas fa-mouse-pointer",
          element: ".calendar-day.has-event",
          showMinimized: true,
        },
        {
          key: "eventDetails",
          icon: "fas fa-list",
          element: ".events-panel",
          showMinimized: true,
        },
        {
          key: "calendarCompletion",
          icon: "fas fa-trophy",
          element: "none",
          showMinimized: false,
        },
      ],
    };
  },

  computed: {
    currentStepData() {
      return this.tutorialSteps[this.currentStep];
    },

    progressPercentage() {
      return ((this.currentStep + 1) / this.tutorialSteps.length) * 100;
    },

    shouldShowMinimized() {
      // Para tutoriales de listados y calendario
      if (
        this.tutorialType === "listings" ||
        this.tutorialType === "calendar"
      ) {
        return (
          this.currentStepData.showMinimized &&
          this.currentStepData.element &&
          this.currentStepData.element !== "none"
        );
      }

      return false;
    },

    // Obtener pasos según el tipo de tutorial
    tutorialSteps() {
      if (this.customSteps) {
        return this.customSteps;
      }

      // Tutoriales disponibles según el tipo
      if (this.tutorialType === "calendar") {
        return this.calendarSteps;
      }

      // Tutorial de listados por defecto
      return this.listingsSteps;
    },
  },

  methods: {
    nextStep() {
      if (this.currentStep < this.tutorialSteps.length - 1) {
        this.currentStep++;

        // Resetear posición si no es un paso minimizado
        if (!this.shouldShowMinimized) {
          this.resetTutorialPosition();
        }

        this.scrollToElement();
      }
    },

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;

        // Resetear posición si no es un paso minimizado
        if (!this.shouldShowMinimized) {
          this.resetTutorialPosition();
        }

        this.scrollToElement();
      }
    },

    goToStep(stepIndex) {
      this.currentStep = stepIndex;

      // Resetear posición si no es un paso minimizado
      if (!this.shouldShowMinimized) {
        this.resetTutorialPosition();
      }

      this.scrollToElement();
    },

    // Métodos para obtener contenido según el tipo de tutorial
    getStepTitle() {
      // Tutoriales de listados y calendario disponibles
      let key;
      if (this.tutorialType === "calendar") {
        key = `calendarTutorial.${this.currentStepData.key}.title`;
      } else {
        key = `tutorial.${this.currentStepData.key}.title`;
      }

      return this.$t(key);
    },

    getStepDescription() {
      // Tutoriales de listados y calendario disponibles
      if (this.tutorialType === "calendar") {
        return this.$t(
          `calendarTutorial.${this.currentStepData.key}.description`
        );
      }
      return this.$t(`tutorial.${this.currentStepData.key}.description`);
    },

    hasStepDetails() {
      // Tutoriales de listados y calendario disponibles
      let details;
      if (this.tutorialType === "calendar") {
        details = this.$t(
          `calendarTutorial.${this.currentStepData.key}.details`
        );
      } else {
        details = this.$t(`tutorial.${this.currentStepData.key}.details`);
      }
      return details && Array.isArray(details) && details.length > 0;
    },

    getStepDetails() {
      // Tutoriales de listados y calendario disponibles
      if (this.tutorialType === "calendar") {
        return (
          this.$t(`calendarTutorial.${this.currentStepData.key}.details`) || []
        );
      }
      return this.$t(`tutorial.${this.currentStepData.key}.details`) || [];
    },

    hasStepTip() {
      // Tutoriales de listados y calendario disponibles
      let tip;
      if (this.tutorialType === "calendar") {
        tip = this.$t(`calendarTutorial.${this.currentStepData.key}.tip`);
      } else {
        tip = this.$t(`tutorial.${this.currentStepData.key}.tip`);
      }
      return tip && tip.trim() !== "";
    },

    getStepTip() {
      // Tutoriales de listados y calendario disponibles
      if (this.tutorialType === "calendar") {
        return (
          this.$t(`calendarTutorial.${this.currentStepData.key}.tip`) || ""
        );
      }
      return this.$t(`tutorial.${this.currentStepData.key}.tip`) || "";
    },

    // Métodos para manejar grupos de campos
    openGroupsContainingElement(element) {
      if (!element) return;

      // Buscar grupos padre que contengan el elemento
      const groups = this.findParentGroups(element);

      groups.forEach((group) => {
        this.openGroup(group);
      });
    },

    findParentGroups(element) {
      const groups = [];
      let currentElement = element;

      // Buscar hacia arriba en el DOM para encontrar grupos
      while (currentElement && currentElement !== document.body) {
        // Buscar diferentes tipos de grupos (ajustar selectores según tu implementación)
        if (this.isGroupElement(currentElement)) {
          groups.unshift(currentElement); // Agregar al inicio para mantener orden
        }
        currentElement = currentElement.parentElement;
      }

      return groups;
    },

    isGroupElement(element) {
      // Selectores específicos para grupos en ExportAI
      const groupSelectors = [
        // Selectores generales
        ".field-group",
        ".form-group",
        ".group-container",
        ".collapsible-group",
        ".accordion-group",
        "[data-group]",
        ".group-field",
        ".field-section",

        // Selectores específicos de ExportAI
        ".field-type-group",
        ".field-group-container",
        ".form-field-group",
        ".dynamic-field-group",
        ".model-field-group",
        ".field-collapsible",
        ".field-expandable",

        // Selectores por atributos
        '[data-field-type="group"]',
        '[data-collapsible="true"]',
        '[data-expandable="true"]',
        "[data-group-type]",

        // Selectores por clases que contengan "group"
        '[class*="group"]',
        '[class*="Group"]',
        '[class*="GROUP"]',
      ];

      return groupSelectors.some((selector) => {
        try {
          return element.matches(selector) || element.closest(selector);
        } catch (error) {
          // Si hay error con el selector, continuar con el siguiente
          return false;
        }
      });
    },

    openGroup(groupElement) {
      if (!groupElement) return;

      // Intentar diferentes métodos para abrir el grupo
      this.tryOpenGroupMethods(groupElement);
    },

    tryOpenGroupMethods(groupElement) {
      // Método 1: Buscar botón de toggle/expandir (más selectores)
      const toggleSelectors = [
        ".group-toggle",
        ".expand-button",
        ".collapse-button",
        ".toggle-button",
        ".expand-btn",
        ".collapse-btn",
        '[data-toggle="collapse"]',
        '[data-toggle="expand"]',
        '[data-action="toggle"]',
        '[data-action="expand"]',
        ".btn-toggle",
        ".btn-expand",
        ".btn-collapse",
        'button[title*="expand"]',
        'button[title*="collapse"]',
        'button[title*="toggle"]',
        "i.fas.fa-chevron-down",
        "i.fas.fa-chevron-right",
        "i.fas.fa-plus",
        "i.fas.fa-minus",
      ];

      const toggleButton = groupElement.querySelector(
        toggleSelectors.join(", ")
      );
      if (toggleButton) {
        toggleButton.click();
        return;
      }

      // Método 2: Buscar elementos con clases de estado colapsado
      const collapsedClasses = [
        "collapsed",
        "closed",
        "folded",
        "hidden",
        "minimized",
      ];
      const expandedClasses = [
        "expanded",
        "open",
        "unfolded",
        "visible",
        "maximized",
      ];

      if (
        collapsedClasses.some((cls) => groupElement.classList.contains(cls))
      ) {
        collapsedClasses.forEach((cls) => groupElement.classList.remove(cls));
        expandedClasses.forEach((cls) => groupElement.classList.add(cls));
      }

      // Método 3: Buscar elementos con atributos de estado
      if (groupElement.getAttribute("aria-expanded") === "false") {
        groupElement.setAttribute("aria-expanded", "true");
      }

      // Método 4: Buscar elementos con data-collapsed
      if (groupElement.getAttribute("data-collapsed") === "true") {
        groupElement.setAttribute("data-collapsed", "false");
      }

      // Método 5: Buscar elementos con estilos de display none
      if (groupElement.style.display === "none") {
        groupElement.style.display = "block";
      }

      // Método 6: Buscar elementos con estilos de height 0 o overflow hidden
      if (
        groupElement.style.height === "0px" ||
        groupElement.style.overflow === "hidden"
      ) {
        groupElement.style.height = "auto";
        groupElement.style.overflow = "visible";
      }

      // Método 7: Buscar elementos con max-height 0
      if (groupElement.style.maxHeight === "0px") {
        groupElement.style.maxHeight = "none";
      }

      // Método 8: Buscar elementos con transform scale(0)
      if (
        groupElement.style.transform &&
        groupElement.style.transform.includes("scale(0)")
      ) {
        groupElement.style.transform = "scale(1)";
      }

      // Método 9: Buscar elementos con opacity 0
      if (groupElement.style.opacity === "0") {
        groupElement.style.opacity = "1";
      }

      // Método 10: Buscar elementos con visibility hidden
      if (groupElement.style.visibility === "hidden") {
        groupElement.style.visibility = "visible";
      }
    },

    // Método para cerrar grupos al completar el tutorial
    closeOpenedGroups() {
      // Aquí podrías implementar lógica para cerrar grupos si es necesario
      // Por ahora solo logueamos la acción
    },

    scrollToElement() {
      // Tutoriales de listados y calendario disponibles
      this.$nextTick(() => {
        if (
          this.currentStepData.element &&
          this.currentStepData.element !== "none"
        ) {
          const element = document.querySelector(this.currentStepData.element);
          if (element) {
            // Abrir grupos que contengan el elemento antes de hacer foco
            this.openGroupsContainingElement(element);

            // Esperar un poco para que los grupos se abran antes de hacer scroll
            setTimeout(() => {
              // Scroll al elemento
              element.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });

              // Si es un paso minimizado, posicionar el tutorial cerca del elemento
              if (this.shouldShowMinimized) {
                this.positionTutorialNearElement(element);
              }
            }, 300); // 300ms de delay para que los grupos se abran
          }
        }
      });
    },

    positionTutorialNearElement(element) {
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const tutorialContainer = document.querySelector(".tutorial-container");

      if (tutorialContainer) {
        // Calcular posición para que el tutorial aparezca cerca del elemento
        let left = rect.right + 20; // 20px a la derecha del elemento
        let top = rect.top - 150; // 150px arriba del elemento

        // Ajustar si se sale de la pantalla
        if (left + 450 > window.innerWidth) {
          left = rect.left - 470; // 20px a la izquierda del elemento
        }

        if (top < 20) {
          top = 20; // Mínimo 20px desde arriba
        }

        if (top + 300 > window.innerHeight) {
          top = window.innerHeight - 320; // Mínimo 20px desde abajo
        }

        // Aplicar posición
        tutorialContainer.style.position = "fixed";
        tutorialContainer.style.left = left + "px";
        tutorialContainer.style.top = top + "px";
        tutorialContainer.style.transform = "none";
        tutorialContainer.style.zIndex = "10001";
      }
    },

    resetTutorialPosition() {
      const tutorialContainer = document.querySelector(".tutorial-container");
      if (tutorialContainer) {
        tutorialContainer.style.position = "";
        tutorialContainer.style.left = "";
        tutorialContainer.style.top = "";
        tutorialContainer.style.transform = "";
        tutorialContainer.style.zIndex = "";
      }
    },

    getHighlightPosition() {
      // Tutoriales de listados y calendario disponibles
      if (
        !this.currentStepData.element ||
        this.currentStepData.element === "none"
      ) {
        return { display: "none" };
      }

      const element = document.querySelector(this.currentStepData.element);
      if (!element) {
        return { display: "none" };
      }

      const rect = element.getBoundingClientRect();
      return {
        position: "fixed",
        top: rect.top + "px",
        left: rect.left + "px",
        width: rect.width + "px",
        height: rect.height + "px",
        zIndex: 9998,
        pointerEvents: "none",
      };
    },

    completeTutorial() {
      // Cerrar grupos abiertos durante el tutorial
      this.closeOpenedGroups();

      // Emitir evento de tutorial completado
      this.$emit("tutorial-completed");
      this.closeTutorial();

      // Guardar en localStorage que el tutorial fue completado
      localStorage.setItem("gotrading-tutorial-completed", "true");
    },

    closeTutorial() {
      // Cerrar grupos abiertos durante el tutorial
      this.closeOpenedGroups();

      this.$emit("close-tutorial");
    },
  },

  mounted() {
    // Verificar si el tutorial ya fue completado
    const tutorialCompleted = localStorage.getItem(
      "gotrading-tutorial-completed"
    );
    if (tutorialCompleted === "true") {
      this.$emit("tutorial-completed");
      this.closeTutorial();
    }
  },

  watch: {
    // Observar cambios en la ruta
    $route: {
      handler(newRoute, oldRoute) {
        if (newRoute && oldRoute && newRoute.path !== oldRoute.path) {
          // Cerrar tutorial automáticamente al cambiar de ruta
          this.closeTutorial();
        }
      },
      immediate: false,
    },
  },
};
</script>

<style scoped>
.tutorial-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  pointer-events: none;
}

.tutorial-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  pointer-events: auto;
}

.tutorial-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 750px; /* Más compacto */
  max-height: 80vh; /* Más compacto */
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.9);
  transition: all 0.3s ease;
}

.tutorial-container.tutorial-active {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* Versión minimizada del tutorial */
.tutorial-container.tutorial-minimized {
  width: 450px;
  max-width: 450px;
  max-height: 500px;
  transform: translate(-50%, -50%) scale(0.8);
}

.tutorial-container.tutorial-minimized .tutorial-header {
  padding: 12px 16px 8px;
}

.tutorial-container.tutorial-minimized .tutorial-content {
  padding: 16px;
  max-height: 400px;
}

.tutorial-container.tutorial-minimized .step-title {
  font-size: 1.3rem;
  margin-bottom: 16px;
}

.tutorial-container.tutorial-minimized .step-description {
  font-size: 1.1rem;
  margin-bottom: 20px;
  line-height: 1.4;
}

.tutorial-container.tutorial-minimized .step-details {
  padding: 16px 20px;
  margin-bottom: 20px;
}

.tutorial-container.tutorial-minimized .detail-item {
  font-size: 1rem;
  margin-bottom: 10px;
  line-height: 1.3;
}

.tutorial-container.tutorial-minimized .step-tip {
  padding: 16px;
}

.tutorial-container.tutorial-minimized .tutorial-navigation {
  padding: 12px 16px 16px;
}

.tutorial-container.tutorial-minimized .step-indicators {
  gap: 6px;
}

.tutorial-container.tutorial-minimized .step-indicator {
  width: 28px;
  height: 28px;
  font-size: 12px;
}

.tutorial-header {
  padding: 20px 25px 16px; /* Más compacto */
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.tutorial-progress {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  margin-right: 12px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4ade80, #22c55e);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.tutorial-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.tutorial-content {
  padding: 25px; /* Más compacto */
  max-height: 450px; /* Más compacto */
  overflow-y: auto;
}

.step-title {
  color: #1f2937;
  margin-bottom: 16px; /* Más compacto */
  font-size: 1.4rem; /* Más pequeño y elegante */
  font-weight: 600;
  text-align: center;
}

.step-description {
  color: #374151;
  font-size: 1.05rem; /* Más pequeño y legible */
  line-height: 1.5; /* Más compacto */
  margin-bottom: 24px; /* Más compacto */
  text-align: center;
}

.step-details {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-left: 4px solid #667eea;
  padding: 20px; /* Más compacto */
  margin-bottom: 24px; /* Más compacto */
  border-radius: 12px;
}

.detail-item {
  color: #1e293b;
  margin-bottom: 12px; /* Más compacto */
  font-size: 0.95rem; /* Más pequeño */
  line-height: 1.4; /* Más compacto */
}

.detail-item:last-child {
  margin-bottom: 0;
}

.step-tip {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 0%, #f59e0b 100%);
  border-left: 4px solid #f59e0b;
  border-radius: 12px;
  padding: 20px; /* Más compacto */
  margin-top: 16px; /* Más compacto */
}

.tip-header {
  color: #92400e;
  font-weight: 600;
  margin-bottom: 8px;
}

.tip-content {
  color: #78350f;
  font-size: 0.9rem; /* Más pequeño */
  line-height: 1.4; /* Más compacto */
}

.tutorial-navigation {
  padding: 16px 25px 20px; /* Más compacto */
  border-top: 1px solid #e9ecef;
  background: #f8fafc;
  border-radius: 0 0 12px 12px;
}

.step-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.step-indicator {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.step-indicator:hover {
  background: #d1d5db;
  transform: scale(1.1);
}

.step-indicator.active {
  background: #3b82f6;
  color: white;
  border-color: #1d4ed8;
  transform: scale(1.1);
}

.step-indicator.completed {
  background: #10b981;
  color: white;
  border-color: #059669;
}

.tutorial-highlight {
  position: fixed;
  border: 3px solid #3b82f6;
  border-radius: 8px;
  background: rgba(59, 130, 246, 0.1);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .tutorial-container {
    width: 95%;
    max-height: 90vh;
  }

  .tutorial-header {
    padding: 16px 20px 12px;
  }

  .tutorial-content {
    padding: 20px;
    max-height: 300px;
  }

  .tutorial-controls {
    flex-direction: column;
    gap: 8px;
  }

  .tutorial-controls .btn {
    width: 100%;
  }

  .step-indicators {
    flex-wrap: wrap;
    gap: 6px;
  }
}
</style>
